/**
 * Created by katarinababic on 6.11.24.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useRootStore } from '../../../Store/useRootStore';
import { usePlausible } from '../../../Contexts/PlausibleContext';
import { useLanguage } from '../../../Hooks/useLanguage';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { Recipe } from '../../../Model/Diet/Recipe/Recipe';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Theme } from '../../../Theme/Theme';
import { ScheduleButton } from '../../ScheduleButton';
import { ShareButton } from '../../ShareButton';
import { ScheduleModal } from '../../ScheduleModal/ScheduleModal';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { EventEntry, EventEntryApi } from '../../../Model/Coach/Schedule/EventEntry';
import { IngredientRow } from './IngredientRow';
import { NutrientRow } from './NutrientRow';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  height: 100vh;
  overflow-y: auto;
  background: #fafafa;
`;

const HeroSection = styled.div<{ image?: string }>`
  position: relative;
  width: 100%;
  height: 400px;
  background: ${({ image }) => (image ? `url('${image}')` : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`)};
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(180deg, transparent, #fafafa);
  }

  @media (min-width: 768px) {
    height: 480px;
  }

  @media (min-width: 1200px) {
    height: 560px;
  }
`;

const ContentContainer = styled.article`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  width: 100%;
  max-width: 900px;
  margin: -80px auto 0;
  padding: 0 24px 64px;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 0 16px 48px;
    margin-top: -60px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 100%;
  text-align: center;
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 28px 20px;
  }
`;

const MetaPillsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const MetaPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${Theme.Colors.textColor};

  i {
    font-size: 16px;
    color: ${Theme.Colors.primary};
  }
`;

const MetaPillSecondary = styled.span`
  color: ${Theme.Colors.warmGrey};
  font-weight: 400;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
  text-align: center;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const GradientLine = styled.div`
  width: min(300px, 60%);
  height: 4px;
  border-radius: 4px;
  background: ${Theme.Colors.primary};
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  line-height: 1.7;
  margin: 0;
  text-align: center;
  max-width: 640px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const TablesSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
`;

const TableTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid ${Theme.Colors.lightestGrey};
`;

const TableRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const TableLabel = styled.span`
  font-size: 0.95rem;
  color: ${Theme.Colors.textColor};
`;

const TableValue = styled.span`
  font-size: 0.95rem;
  color: ${Theme.Colors.charcoal};
  text-align: right;
`;

const InstructionsCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  width: 100%;
`;

const InstructionsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 24px 0;
`;

const StepsList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step-counter;
`;

const StepItem = styled.li`
  display: flex;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  counter-increment: step-counter;

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: counter(step-counter);
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark});
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
  }
`;

const StepText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: ${Theme.Colors.textColor};
  line-height: 1.6;
  padding-top: 4px;
`;

const RecipeSource = styled.p`
  font-size: 0.875rem;
  color: ${Theme.Colors.warmGrey};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

const nutrients: string[] = [
  'calories',
  'carbohydrates',
  'sugar',
  'fat',
  'saturated_fat',
  'sodium',
  'protein',
  'fiber',
];

export type ExploreContentViewRecipeProps = {
  entry: Recipe;
};

export const ExploreContentViewRecipe: React.FC<ExploreContentViewRecipeProps> = observer(({ entry }) => {
  const { engagement } = useRootStore();
  const { trackPlausible } = usePlausible();
  const { language } = useLanguage();
  const title = useLocalized(entry.name);
  const { t } = useTrans();
  const entryImage = useLocalizedImage(entry.image ? [entry.image] : []);

  const image = entryImage?.url;
  const recipeSource = entry.sourceName ?? entry.creditsText ?? 'kinastic';
  const calories = Math.floor(entry.nutrition?.nutrients.calories?.amount || 0);
  const duration = Math.floor(entry.readyInMinutes || 0);
  const servings = Math.floor(entry.servings || 0);

  const [showSchedule, setShowSchedule] = React.useState(false);
  const [eventEntry, setEventEntry] = React.useState<EventEntry | undefined>(undefined);

  const loadEvent = React.useCallback(() => {
    EventEntryApi.findParent({ objectType: entry.type, objectId: entry.id }).then((e) => setEventEntry(e));
  }, [entry.type, entry.id]);

  React.useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  React.useEffect(() => {
    trackPlausible('Page View', { pageTitle: title, contentType: entry.type, contentId: entry.id });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, title, entry.type]);

  const handleShowScheduleModal = React.useCallback(() => setShowSchedule(true), []);

  const handleCloseScheduleModal = React.useCallback(() => {
    setShowSchedule(false);
    loadEvent();
  }, [loadEvent]);

  const hasTriggeredRef = React.useRef(false);
  const prevScrollYRef = React.useRef(0); // Track previous scroll position
  const openedTimestampRef = React.useRef<number>(Date.now());

  // Reset tracking when entry changes
  React.useEffect(() => {
    openedTimestampRef.current = Date.now();
    hasTriggeredRef.current = false;
  }, [entry.id]);

  const handleScrollEnd = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      try {
        const target = event?.currentTarget;
        if (!target || !target.scrollHeight || hasTriggeredRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = target;
        const currentScrollY = scrollTop;

        const isScrollingDown = currentScrollY > prevScrollYRef.current;
        prevScrollYRef.current = currentScrollY;

        if (!isScrollingDown) return;

        const isCloseToEnd = scrollTop + clientHeight >= scrollHeight - 96;

        if (isCloseToEnd) {
          hasTriggeredRef.current = true;
          const durationSeconds = Math.round((Date.now() - openedTimestampRef.current) / 1000);
          engagement.sendRecipeRead(entry, durationSeconds).then((res) => engagement.showPointsModalPromise(res));
          trackPlausible('Content Complete', {
            seconds: `${durationSeconds}s`,
            pageTitle: title,
            contentType: entry.type,
            contentId: entry.id,
          });
        }
      } catch (error) {
        console.error('Scroll handler error:', error);
      }
    },
    [engagement, entry, trackPlausible],
  );

  const summary = useLocalized(entry.summary);

  useContentMetaTags({ title, description: summary, imageUrl: image });

  const allSteps = entry.instructions.flatMap((instruction) =>
    instruction.steps.map((step) => step.getStepText(language)),
  );

  return (
    <PageContainer onScroll={handleScrollEnd}>
      <HeroSection image={image} />

      <ContentContainer>
        <HeaderSection>
          <MetaPillsGroup>
            <MetaPill>
              <i className="bi bi-clock" />
              {duration} {t('globals.time.minutes', { defaultValue: 'min' })}
            </MetaPill>
            {calories > 0 && (
              <MetaPill>
                <i className="bi bi-fire" />
                {calories} kcal
                <MetaPillSecondary>{t('explore.dietTab.recipeScreen.perServing', { defaultValue: 'per serving' })}</MetaPillSecondary>
              </MetaPill>
            )}
            <MetaPill>
              <i className="bi bi-cup-hot" />
              {servings} {t('explore.dietTab.recipeScreen.servingsLabel', { defaultValue: 'servings' })}
            </MetaPill>
          </MetaPillsGroup>

          <TitleSection>
            <Title>{title}</Title>
            <GradientLine />
          </TitleSection>

          {summary && <Description>{summary}</Description>}

          <ActionsRow>
            <ShareButton data={{ title, description: summary, imageUrl: image, contentType: 'recipe', contentId: entry.id }} />
            <ScheduleButton event={eventEntry} onClick={handleShowScheduleModal} />
          </ActionsRow>
        </HeaderSection>

        <TablesSection>
          <TableCard>
            <TableTitle>{t('explore.dietTab.recipeScreen.ingredients', { defaultValue: 'Ingredients' })}</TableTitle>
            {entry.ingredients.map((ingredient) => (
              <IngredientRow key={ingredient.id} ingredient={ingredient} />
            ))}
          </TableCard>

          <TableCard>
            <TableTitle>
              {t('explore.dietTab.recipeScreen.nutritionInformation.title', { defaultValue: 'Nutrition' })}
            </TableTitle>
            {nutrients.map((n) => {
              const nutrient = entry.nutrition?.nutrients[n];
              if (!nutrient) return null;
              return <NutrientRow key={n} nutrientKey={n} nutrient={nutrient} />;
            })}
          </TableCard>
        </TablesSection>

        {allSteps.length > 0 && (
          <InstructionsCard>
            <InstructionsTitle>
              {t('explore.dietTab.recipeScreen.cookingInstructions', { defaultValue: 'Instructions' })}
            </InstructionsTitle>
            <StepsList>
              {allSteps.map((step, index) => (
                <StepItem key={index}>
                  <StepText>{step}</StepText>
                </StepItem>
              ))}
            </StepsList>
          </InstructionsCard>
        )}

        <RecipeSource>
          {t('explore.dietTab.recipeScreen.source', { defaultValue: 'by {{value}}', value: recipeSource })}
        </RecipeSource>
      </ContentContainer>

      {showSchedule && (
        <ScheduleModal
          key={eventEntry?.id ?? entry.id}
          exploreEntry={entry}
          parentEvent={eventEntry}
          currentEvent={eventEntry}
          disableRecurring={false}
          onSchedule={handleCloseScheduleModal}
          onDelete={handleCloseScheduleModal}
        />
      )}
    </PageContainer>
  );
});
